import { useState, type FormEvent } from 'react';
import { formatMoney } from '@vns/core';
import { useLookups, todayLocal, normalizeUnitLabel } from '../lib/lookups';
import {
  useCreateBill,
  emptyLine,
  lineTotalPaise,
  linesTotalPaise,
  billDifferencePaise,
  validateBill,
  billHasErrors,
  type BillDraft,
  type BillLine,
  type BillErrors,
} from '../lib/purchase-bills';
import { Drawer, Field, useToast, Select } from './ui';
import {
  Plus,
  Trash2,
  Calendar,
  Building2,
  FileText,
  DollarSign,
  AlertCircle,
  Info,
  Layers,
  Sparkles,
  ShoppingBag,
} from 'lucide-react';

/**
 * One supplier purchase bill with multiple material line items.
 * Written by a single atomic database mutation.
 */
export default function BillDrawer({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { data: lk, isLoading } = useLookups();
  const create = useCreateBill(projectId);
  const toast = useToast();

  const [draft, setDraft] = useState<BillDraft>({
    date: todayLocal(),
    supplier_id: '',
    bill_no: '',
    billTotalRupees: '',
    remarks: '',
    lines: [emptyLine()],
  });
  const [errors, setErrors] = useState<BillErrors>({ lines: {} });
  const [touched, setTouched] = useState(false);

  const set = <K extends keyof BillDraft>(k: K, v: BillDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const setLine = (i: number, patch: Partial<BillLine>) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((l, n) => (n === i ? { ...l, ...patch } : l)),
    }));

  const addLine = () => setDraft((d) => ({ ...d, lines: [...d.lines, emptyLine()] }));

  const removeLine = (i: number) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.length > 1 ? d.lines.filter((_, n) => n !== i) : [emptyLine()],
    }));

  const linesTotal = linesTotalPaise(draft.lines);
  const filledCount = draft.lines.filter((l) => lineTotalPaise(l) !== null).length;
  const difference = billDifferencePaise(draft);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    const found = validateBill(draft);
    setErrors(found);
    if (billHasErrors(found)) return;

    try {
      await create.mutateAsync(draft);
      toast.show(`Purchase recorded — ${filledCount} material${filledCount === 1 ? '' : 's'}`);
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the purchase', true);
    }
  }

  const revalidate = (next: BillDraft) => {
    if (touched) setErrors(validateBill(next));
  };

  return (
    <Drawer
      title="Add Supplier Purchase"
      description="Record multi-line materials delivered on a supplier bill"
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Save Purchase Entry"
      busy={create.isPending}
      wide
    >
      <div className="space-y-4">
        {/* Top Form Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-slate-50/70 p-3.5 sm:p-4 rounded-[10px] border border-slate-200/80">
          <Field label="Bill or Invoice Date" error={errors.date}>
            <input
              type="date"
              value={draft.date}
              onChange={(e) => set('date', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>

          <Field label="Supplier" error={errors.supplier_id}>
            <Select
              value={draft.supplier_id}
              disabled={isLoading}
              placeholder={isLoading ? 'Loading…' : 'Choose a supplier'}
              options={[
                { value: '', label: isLoading ? 'Loading…' : 'Choose a supplier' },
                ...(lk?.suppliers?.map((s) => ({ value: s.id, label: s.name })) ?? []),
              ]}
              className={errors.supplier_id ? 'border-rose-500' : ''}
              onChange={(val) => {
                const next = { ...draft, supplier_id: val };
                setDraft(next);
                revalidate(next);
              }}
            />
          </Field>

          <Field label="Invoice / Bill No." optional hint="Copied onto material lines">
            <input
              value={draft.bill_no}
              maxLength={40}
              onChange={(e) => set('bill_no', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
            />
          </Field>

          <Field
            label="Printed Total (₹)"
            optional
            error={errors.billTotalRupees}
            hint="If bill total differs from lines sum"
          >
            <input
              value={draft.billTotalRupees}
              inputMode="decimal"
              aria-invalid={!!errors.billTotalRupees}
              onChange={(e) => {
                const next = { ...draft, billTotalRupees: e.target.value };
                setDraft(next);
                revalidate(next);
              }}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-semibold"
            />
          </Field>
        </div>

        {/* Multi-Line Material Table Panel */}
        <div className="bg-white rounded-[10px] border border-slate-200/80 p-3.5 sm:p-4 space-y-3 shadow-xs">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-800 m-0 flex items-center gap-1.5">
                <ShoppingBag className="w-4 h-4 text-sky-600" />
                Materials on this Purchase Bill
              </h4>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                Enter each material, quantity received, unit, and agreed unit purchase rate.
              </p>
            </div>
            {errors.form && (
              <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2.5 py-0.5 rounded-[6px] border border-rose-200">
                {errors.form}
              </span>
            )}
          </div>

          <div className="bill-panel p-0 bg-transparent border-0">
            <div className="bill-line-head hidden sm:grid text-[11px] text-slate-400 font-bold uppercase tracking-wider pb-2 border-b border-slate-200/80">
              <div>Material Item</div>
              <div className="text-center">Quantity</div>
              <div className="text-center">Unit</div>
              <div className="text-center">Rate (₹)</div>
              <div className="text-right">Line Amount</div>
              <div />
            </div>

            <div className="bill-lines space-y-2 pt-2">
              {draft.lines.map((line, i) => {
                const le = errors.lines[i] ?? {};
                const amount = lineTotalPaise(line);
                return (
                  <div key={i} className="bill-line items-center gap-2 bg-slate-50/50 p-2 rounded-[8px] border border-slate-200/60 hover:bg-slate-50 transition-colors">
                    {/* Material Selector */}
                    <div className="field material">
                      <Select
                        size="sm"
                        value={line.material_id}
                        disabled={isLoading}
                        placeholder={isLoading ? 'Loading…' : 'Choose a material'}
                        options={[
                          { value: '', label: isLoading ? 'Loading…' : 'Choose a material' },
                          ...(lk?.materials?.map((m) => ({ value: m.id, label: m.name })) ?? []),
                        ]}
                        className={le.material_id ? 'border-rose-500' : ''}
                        onChange={(matId) => {
                          const mat = lk?.materials?.find((m) => m.id === matId);
                          const next = {
                            ...draft,
                            lines: draft.lines.map((l, n) =>
                              n === i
                                ? {
                                    ...l,
                                    material_id: matId,
                                    unit_id: l.unit_id || mat?.unit_id || '',
                                    rateRupees:
                                      l.rateRupees || (mat?.rate_paise ? String(mat.rate_paise / 100) : ''),
                                  }
                                : l,
                            ),
                          };
                          setDraft(next);
                          revalidate(next);
                        }}
                      />
                      {le.material_id && <div className="text-[10px] text-rose-600 mt-0.5">{le.material_id}</div>}
                    </div>

                    {/* Quantity */}
                    <div className="field qty">
                      <input
                        id={`q-${i}`}
                        value={line.quantity}
                        inputMode="decimal"
                        aria-invalid={!!le.quantity}
                        onChange={(e) => {
                          const next = {
                            ...draft,
                            lines: draft.lines.map((l, n) =>
                              n === i ? { ...l, quantity: e.target.value } : l,
                            ),
                          };
                          setDraft(next);
                          revalidate(next);
                        }}
                        className="w-full px-2.5 py-1.5 text-xs text-center bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 font-mono font-semibold"
                      />
                      {le.quantity && <div className="text-[10px] text-rose-600 mt-0.5">{le.quantity}</div>}
                    </div>

                    {/* Unit */}
                    <div className="field unit">
                      <Select
                        size="sm"
                        value={line.unit_id}
                        disabled={isLoading}
                        placeholder="—"
                        options={[
                          { value: '', label: '—' },
                          ...(lk?.units?.map((u) => ({ value: u.id, label: normalizeUnitLabel(u.name, u.short_name) })) ?? []),
                        ]}
                        onChange={(val) => setLine(i, { unit_id: val })}
                      />
                    </div>

                    {/* Rate */}
                    <div className="field rate">
                      <input
                        id={`r-${i}`}
                        value={line.rateRupees}
                        inputMode="decimal"
                        aria-invalid={!!le.rateRupees}
                        onChange={(e) => {
                          const next = {
                            ...draft,
                            lines: draft.lines.map((l, n) =>
                              n === i ? { ...l, rateRupees: e.target.value } : l,
                            ),
                          };
                          setDraft(next);
                          revalidate(next);
                        }}
                        className="w-full px-2.5 py-1.5 text-xs text-center bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 font-mono font-semibold"
                      />
                      {le.rateRupees && <div className="text-[10px] text-rose-600 mt-0.5">{le.rateRupees}</div>}
                    </div>

                    {/* Amount */}
                    <div className="amount text-right pr-2 font-mono font-bold text-xs sm:text-sm text-slate-800 tabular-nums">
                      {amount === null ? (
                        <span className="text-slate-300 font-normal">—</span>
                      ) : (
                        formatMoney(amount)
                      )}
                    </div>

                    {/* Remove Line Button */}
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      title="Remove this line"
                      aria-label={`Remove line ${i + 1}`}
                      className="btn-remove-line rm"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addLine}
              className="btn-add-line"
            >
              <Plus size={14} strokeWidth={2.2} />
              <span>Add Another Material Line</span>
            </button>
          </div>
        </div>

        {/* Totals & Freight Summary Banner */}
        <div className="drawer-summary-banner" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="title">
              Total Material Lines ({filledCount} entered)
            </span>
            <span className="amount">
              {formatMoney(linesTotal)}
            </span>
          </div>

          {difference !== null && difference !== 0 && (
            <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-soft)' }}>
                <span>Printed Total on Invoice</span>
                <span className="amount" style={{ fontSize: 13 }}>
                  {formatMoney(linesTotal + difference)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11.5, color: '#b45309', background: '#fef3c7', padding: '6px 10px', borderRadius: 'var(--radius-xs)', border: '1px solid #fde68a' }}>
                <Info size={13} style={{ color: '#d97706', marginTop: 2, flexShrink: 0 }} />
                <span>
                  {difference > 0
                    ? `Bill is ${formatMoney(difference)} higher than the material lines (freight, cartage or GST loading).`
                    : `Material lines sum is ${formatMoney(-difference)} higher than printed invoice total.`}{' '}
                  Saved as entered for verification during admin review.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Remarks Field */}
        <div>
          <Field label="Delivery / Purchase Remarks" optional span2>
            <textarea
              rows={2}
              value={draft.remarks}
              maxLength={300}
              onChange={(e) => set('remarks', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>
        </div>
      </div>
    </Drawer>
  );
}
